import { useCallback, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import {
  ArrowUpRight,
  Banknote,
  BriefcaseBusiness,
  Building2,
  ChartColumn,
  CircleAlert,
  Database,
  HardHat,
  Maximize2,
  ShieldCheck,
  TrendingUp,
  Users,
} from 'lucide-react';
import { Badge, cn, MetricCard, RiskBadge } from '@helios/ui';
import type { DrillCohort } from '@helios/ui/charts';
import type {
  CommandCenterPayload,
  CommandCenterProjectMarker,
} from '@/services/commandCenterClient';
import { formatRecordedAmount } from '@/features/finance/financeInvoices.types';
import { companyMetricScope } from '@/features/finance/financeOverview.view';
import './executive-overview.css';

type Project = CommandCenterProjectMarker;
export interface ExecutiveOverviewNavigation {
  section: string;
  group: string;
}
type Props = {
  payload: CommandCenterPayload;
  permissions: string[];
  priorities?: ReactNode;
  navigation?: ExecutiveOverviewNavigation;
  onNavigationChange?: (navigation: ExecutiveOverviewNavigation) => void;
  inspect: (cohort: DrillCohort) => void;
  healthScore: (project: Project) => number | null;
  healthCoverage: string;
  healthSourceAvailable: boolean;
  onInspectCompany: () => void;
  onInspectCash: () => void;
  onInspectForecast: (state?: string) => void;
  onInspectDecisions: () => void;
  onInspectField: () => void;
  onInspectOperations: () => void;
  onInspectProject: (project: Project) => void;
  onInspectHealth: (band: string) => void;
};
const forecastStates = [
  'current',
  'unreviewed',
  'currency_unknown',
  'missing',
  'stale',
  'inconsistent',
  'contract_changed',
  'restricted',
] as const;
const healthBands = ['90-100', '75-89', '60-74', '<60', 'UNAVAILABLE'] as const;
const overviewSections = [
  { id: 'company', label: 'Finance' },
  { id: 'forecast', label: 'Forecasts' },
  { id: 'decisions', label: 'Decisions & priorities' },
  { id: 'portfolio', label: 'Portfolio' },
  { id: 'growth', label: 'Growth' },
  { id: 'operations', label: 'Operating' },
  { id: 'coverage', label: 'Sources & evidence' },
] as const;
const overviewGroups = [
  { id: 'summary', label: 'Executive summary' },
  { id: 'coverage', label: 'Sources & evidence' },
] as const;
type OverviewLayout = 'grid' | 'grouped' | 'tabs';
const riskOrder = ['CRITICAL', 'HIGH', 'MODERATE', 'LOW'] as const;
const label = (value: string) =>
  value
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/^./, (s) => s.toUpperCase());
const count = (value: number | null | undefined) =>
  value === null || value === undefined || !Number.isFinite(value)
    ? 'Unavailable'
    : new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value);
const decimal = (value: string | null | undefined) => {
  if (!value?.trim() || !/^[+-]?\d+(?:\.\d+)?$/.test(value)) return null;
  const result = Number(value);
  return Number.isFinite(result) ? result : null;
};
const compactAmount = (value: string | null | undefined) => {
  const amount = decimal(value);
  return amount === null
    ? null
    : new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 2,
      }).format(amount);
};
const shareOfRevenue = (value: string | null | undefined, revenue: string | null | undefined) => {
  const numerator = decimal(value),
    denominator = decimal(revenue);
  if (numerator === null || denominator === null || denominator === 0) return 'Unavailable';
  const share = (numerator / denominator) * 100;
  return Number.isFinite(share) ? `${share.toFixed(2)}%` : 'Unavailable';
};

/** The overview summarizes the same native sources and evidence actions as its detail tabs. */
export function ExecutiveOverview({
  payload,
  permissions,
  priorities,
  navigation,
  onNavigationChange,
  inspect,
  healthScore,
  healthCoverage,
  healthSourceAvailable,
  onInspectCompany,
  onInspectCash,
  onInspectForecast,
  onInspectDecisions,
  onInspectField,
  onInspectOperations,
  onInspectProject,
  onInspectHealth,
}: Props) {
  const workspace = useRef<HTMLDivElement>(null);
  const metrics = useRef<HTMLElement>(null);
  const prefix = useId();
  const [layout, setLayout] = useState<OverviewLayout>('grid');
  const [localNavigation, setLocalNavigation] = useState<ExecutiveOverviewNavigation>({
    section: 'company',
    group: 'summary',
  });
  const { section: activeSection, group: activeGroup } = navigation ?? localNavigation;
  const updateNavigation = useCallback(
    (next: ExecutiveOverviewNavigation) => {
      if (navigation === undefined) setLocalNavigation(next);
      onNavigationChange?.(next);
    },
    [navigation, onNavigationChange],
  );
  const restoreFocus = useRef<string | null>(null);
  const tabbed = layout === 'tabs';
  const grouped = layout === 'grouped';
  const visibleTabs = grouped ? overviewGroups : overviewSections;
  const activeTab = grouped ? activeGroup : activeSection;
  const tabId = (id: string) => `${prefix}-${grouped ? 'group' : 'tab'}-${id}`;
  const tabPanelId = (id: string) =>
    grouped && id === 'summary' ? `${prefix}-summary` : `${prefix}-panel-${id}`;
  const selectTab = (id: string) => {
    updateNavigation(
      grouped
        ? {
            group: id,
            section:
              id === 'coverage'
                ? 'coverage'
                : activeSection === 'coverage'
                  ? 'company'
                  : activeSection,
          }
        : {
            section: id,
            group: id === 'coverage' ? 'coverage' : 'summary',
          },
    );
  };
  useLayoutEffect(() => {
    const element = workspace.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const { width, height } = element.getBoundingClientRect();
      if (width <= 0 || height <= 0) return;
      const phone = window.matchMedia('(max-width: 767px)').matches;
      const metricsHeight = metrics.current?.getBoundingClientRect().height ?? 88;
      const rem = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      // Both decisions use the stable workspace allocation, not the currently
      // visible grid, so adding/removing tabs cannot create a resize feedback loop.
      const rowsHeight = 23.625 * rem;
      const gridHeight = height - metricsHeight - 9.5 * rem - rem;
      const summaryHeight = height - metricsHeight - 2 * rem - rem;
      const next: OverviewLayout = phone
        ? 'grid'
        : width < 1120
          ? 'tabs'
          : gridHeight >= rowsHeight
            ? 'grid'
            : summaryHeight >= rowsHeight
              ? 'grouped'
              : 'tabs';
      if (next === layout) return;
      const focused =
        document.activeElement instanceof HTMLElement && element.contains(document.activeElement)
          ? document.activeElement
          : null;
      const focusedSection =
        focused?.closest<HTMLElement>('[data-overview-section]')?.dataset.overviewSection;
      const section = focusedSection ?? activeSection;
      const sourcesSelected = focusedSection
        ? focusedSection === 'coverage'
        : layout === 'grouped'
          ? activeGroup === 'coverage'
          : activeSection === 'coverage';
      const nextNavigation = { section: focusedSection ?? activeSection, group: activeGroup };
      if (next === 'grouped') nextNavigation.group = sourcesSelected ? 'coverage' : 'summary';
      if (next === 'tabs') nextNavigation.section = sourcesSelected ? 'coverage' : section;
      if (nextNavigation.section !== activeSection || nextNavigation.group !== activeGroup)
        updateNavigation(nextNavigation);
      if (focused?.closest('.executive-overview__tabs')) {
        restoreFocus.current =
          next === 'grid'
            ? `${prefix}-panel-${section}`
            : next === 'grouped'
              ? `${prefix}-group-${sourcesSelected ? 'coverage' : 'summary'}`
              : `${prefix}-tab-${sourcesSelected ? 'coverage' : section}`;
      }
      setLayout(next);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    if (metrics.current) observer.observe(metrics.current);
    measure();
    return () => observer.disconnect();
  }, [activeGroup, activeSection, layout, prefix, updateNavigation]);
  useLayoutEffect(() => {
    const id = restoreFocus.current;
    if (!id) return;
    restoreFocus.current = null;
    const target = document.getElementById(id);
    if (!target || !workspace.current?.contains(target)) return;
    const control = target.matches('[role="tab"]')
      ? target
      : target.querySelector<HTMLElement>('.executive-overview__panel-body');
    control?.focus({ preventScroll: true });
  }, [layout]);
  const sectionProps = (id: string) => ({
    id: `${prefix}-panel-${id}`,
    sectionId: id,
    hidden: tabbed
      ? activeSection !== id
      : grouped && id === 'coverage' && activeGroup !== 'coverage',
    tabLabelId: tabbed
      ? `${prefix}-tab-${id}`
      : grouped && id === 'coverage'
        ? `${prefix}-group-coverage`
        : undefined,
  });
  const read = payload.executive;
  // A source that loses access or availability must never revive a retained payload.
  const finance = read?.finance.state === 'available' ? read.finance.data : null;
  const owner = read?.owner.state === 'available' ? read.owner.data : null;
  const operations = read?.operations.state === 'available' ? read.operations.data : null;
  const growth = read?.growth?.state === 'available' ? read.growth.data : null;
  const company = hasPermission(permissions, PERMISSIONS.ACCOUNTING_CONNECTIONS_READ)
    ? finance?.accounting
    : null;
  const companyScope = companyMetricScope(company, 'revenue');
  const canReadProjects =
    (payload.coverage?.canReadProjects ?? true) &&
    hasPermission(permissions, PERMISSIONS.PROJECTS_READ);
  const projects = canReadProjects ? payload.projectMap.markers : [];
  const decisions = owner?.decisions;
  const forecasts = finance?.profitability;
  const current = forecasts?.filter((row) => row.forecastCoverage === 'current').length;
  const field = operations?.fieldToday;
  const cashAvailable = Boolean(
    finance?.cashSource?.currency && finance.cashSource.source !== 'unavailable',
  );
  const sources = read
    ? [read.finance, read.owner, read.operations, read.growth, read.invoices].filter(
        (source) => source !== undefined,
      )
    : [];
  const exceptionRows = [...projects]
    .filter(
      (row) =>
        ['HIGH', 'CRITICAL'].includes(row.riskLevel) ||
        (healthScore(row) !== null && healthScore(row)! < 75),
    )
    .sort(
      (a, b) =>
        riskOrder.indexOf(a.riskLevel) - riskOrder.indexOf(b.riskLevel) ||
        (healthScore(a) ?? Infinity) - (healthScore(b) ?? Infinity),
    );
  const topProject = [...projects].sort(
    (a, b) =>
      riskOrder.indexOf(a.riskLevel) - riskOrder.indexOf(b.riskLevel) ||
      (healthScore(a) ?? Infinity) - (healthScore(b) ?? Infinity),
  )[0];
  const open = (available: boolean, action: () => void, name: string) => {
    if (available) action();
    else
      inspect({
        id: `unavailable:${name}`,
        label: name,
        measure: 'Source evidence',
        basis:
          'The current authorized source is unavailable or restricted. Refresh or inspect the owning workspace.',
        records: [],
        recordsAvailable: false,
        recordsUnavailableReason: 'Current source access and available evidence are required.',
      });
  };
  const forecastCount = (state: string) =>
    forecasts?.filter((row) => (row.forecastCoverage ?? 'missing') === state).length;
  return (
    <div
      ref={workspace}
      className={cn(
        'executive-overview',
        tabbed && 'executive-overview--tabbed',
        grouped && 'executive-overview--grouped',
      )}
      data-executive-overview=""
      data-overview-layout={layout}
    >
      <section
        ref={metrics}
        className="executive-overview__metrics glass"
        aria-label="Executive source measures"
      >
        <MetricCard
          className="executive-overview__metric"
          label="Company revenue"
          value={compactAmount(company?.revenue)}
          icon={<Banknote />}
          hint={
            company
              ? `${companyScope.currency || 'Currency unreported'} · ${companyScope.basis || 'basis unreported'}`
              : 'Imported company statement required'
          }
          onClick={() =>
            open(Boolean(company), onInspectCompany, 'Imported company report evidence')
          }
        />
        <MetricCard
          className="executive-overview__metric"
          label="Recorded cash"
          value={cashAvailable ? compactAmount(finance?.summary.netCashPosition) : null}
          icon={<Banknote />}
          hint={
            finance?.cashSource?.currency
              ? `${finance.cashSource.currency} · ${finance.cashSource.source === 'confirmed_accounts' ? 'confirmed account group' : 'imported balance sheet'}`
              : 'Common currency and confirmed source required'
          }
          onClick={() => open(cashAvailable, onInspectCash, 'Recorded cash source')}
        />
        <MetricCard
          className="executive-overview__metric"
          label="Forecast coverage"
          value={current === undefined ? null : `${current} / ${forecasts?.length}`}
          hint="Current qualifying forecasts"
          icon={<ShieldCheck />}
          onClick={() =>
            open(Boolean(finance), () => onInspectForecast(), 'Project forecast evidence')
          }
        />
        <MetricCard
          className="executive-overview__metric"
          label="Active / on hold"
          value={canReadProjects ? payload.summary.activeProjects : null}
          hint={
            canReadProjects
              ? `${projects.length} portfolio records · selected filters`
              : 'Project source access required'
          }
          icon={<Building2 />}
          onClick={() =>
            inspect({
              id: 'active-projects',
              label: 'Active and on-hold projects',
              measure: 'Project records',
              value: count(canReadProjects ? payload.summary.activeProjects : null),
              basis: payload.coverage?.projectCohort ?? 'Selected recorded portfolio',
              records: projects
                .filter((row) => ['ACTIVE', 'ON_HOLD'].includes(row.status))
                .map((row) => ({
                  id: row.id,
                  title: `${row.projectNumber} · ${row.name}`,
                  subtitle: `${label(row.riskLevel)} recorded risk`,
                  href: `/projects/overview?projectId=${encodeURIComponent(row.id)}`,
                })),
              recordsAvailable: canReadProjects,
              recordsUnavailableReason: 'Project source access is required.',
              viewAllHref: canReadProjects ? '/projects' : undefined,
            })
          }
        />
        <MetricCard
          className="executive-overview__metric"
          label="Source decisions"
          value={decisions?.total ?? null}
          icon={<BriefcaseBusiness />}
          hint={decisions ? 'Permitted source cohort' : 'Permitted native sources required'}
          onClick={() => open(Boolean(decisions), onInspectDecisions, 'Waiting source decisions')}
        />
        <MetricCard
          className="executive-overview__metric"
          label="Reported on site"
          value={field?.totalHeadcount ?? null}
          icon={<Users />}
          hint={
            field
              ? `${field.date} · ${field.timeZone} · ${field.totalCrews} reported crews`
              : 'Current field report source required'
          }
          onClick={() => open(Boolean(field), onInspectField, 'Current field reporting evidence')}
        />
      </section>

      {(tabbed || grouped) && (
        <div
          role="tablist"
          aria-label={grouped ? 'Executive overview groups' : 'Executive overview sections'}
          className="executive-overview__tabs"
          onKeyDown={(event) => {
            const controls = Array.from(
              event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'),
            );
            const index = controls.indexOf(event.target as HTMLButtonElement);
            if (index < 0) return;
            const next =
              event.key === 'ArrowRight'
                ? (index + 1) % controls.length
                : event.key === 'ArrowLeft'
                  ? (index - 1 + controls.length) % controls.length
                  : event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                      ? controls.length - 1
                      : null;
            const section = next === null ? undefined : visibleTabs[next];
            if (!section || next === null) return;
            event.preventDefault();
            selectTab(section.id);
            controls[next]?.focus();
          }}
        >
          {visibleTabs.map((section) => (
            <button
              key={section.id}
              id={tabId(section.id)}
              type="button"
              role="tab"
              aria-selected={activeTab === section.id}
              aria-controls={tabPanelId(section.id)}
              tabIndex={activeTab === section.id ? 0 : -1}
              className="hx-section-tab"
              onClick={() => selectTab(section.id)}
            >
              {section.label}
            </button>
          ))}
        </div>
      )}
      <div
        className="executive-overview__grid"
        id={`${prefix}-summary`}
        role={grouped ? 'tabpanel' : undefined}
        aria-labelledby={grouped ? `${prefix}-group-summary` : undefined}
        tabIndex={grouped ? 0 : undefined}
        hidden={grouped ? activeGroup === 'coverage' : tabbed && activeSection === 'coverage'}
      >
        <OverviewPanel
          {...sectionProps('company')}
          title="Company financial position"
          icon={<ChartColumn />}
          className="executive-overview__company"
          onInspect={() =>
            open(Boolean(company), onInspectCompany, 'Imported company report evidence')
          }
          detail={
            company
              ? `${companyScope.dateLabel} · ${companyScope.basis || 'Basis unreported'} · ${companyScope.currency || 'Currency unreported'}`
              : 'Imported statement unavailable'
          }
        >
          <table className="executive-overview__table" aria-label="Company financial statement">
            <thead>
              <tr>
                <th>Metric</th>
                <th className="numeric">Recorded amount</th>
                <th className="numeric">Share of revenue</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Revenue', company?.revenue, '—'],
                [
                  'Gross profit',
                  company?.grossProfit,
                  shareOfRevenue(company?.grossProfit, company?.revenue),
                ],
                [
                  'Net income',
                  company?.netIncome,
                  shareOfRevenue(company?.netIncome, company?.revenue),
                ],
              ].map(([name, value, share]) => (
                <tr key={name}>
                  <th scope="row">{name}</th>
                  <td className="numeric">
                    {formatRecordedAmount(value)}
                    {companyScope.currency ? ` ${companyScope.currency}` : ''}
                  </td>
                  <td className="numeric">{share}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="executive-overview__footnote">
            <span>Margins calculated from statement; comparison and annual plan unavailable.</span>
            <button
              type="button"
              onClick={() =>
                open(Boolean(company), onInspectCompany, 'Imported company report evidence')
              }
            >
              Inspect report basis <ArrowUpRight aria-hidden="true" />
            </button>
          </div>
          <p className="executive-overview__note">
            {company?.lastSyncedAt
              ? `Completed import ${new Date(company.lastSyncedAt).toLocaleString()}`
              : 'No completed company import is available.'}
          </p>
        </OverviewPanel>

        <OverviewPanel
          {...sectionProps('forecast')}
          title="Forecast evidence coverage"
          icon={<ShieldCheck />}
          className="executive-overview__forecast"
          onInspect={() =>
            open(Boolean(finance), () => onInspectForecast(), 'Project forecast evidence')
          }
          detail={
            forecasts
              ? `${current} / ${forecasts.length} current qualifying forecasts`
              : 'Finance source unavailable'
          }
        >
          {!forecasts ? (
            <SourceGap>
              {read?.finance.note ?? 'Finance source unavailable or restricted.'}
            </SourceGap>
          ) : (
            <>
              <div className="executive-overview__forecast-summary">
                <strong>
                  {current} / {forecasts.length} current
                </strong>
                <span>{forecastCount('missing')} missing</span>
              </div>
              {forecasts.length > 0 ? (
                <div className="executive-overview__coverage-meter" aria-hidden="true">
                  {forecastStates.map((state) => (
                    <span
                      key={state}
                      data-coverage-state={state}
                      style={{
                        width: `${((forecastCount(state) ?? 0) / forecasts.length) * 100}%`,
                      }}
                    />
                  ))}
                </div>
              ) : null}
              <div
                className="executive-overview__status-grid"
                aria-label="Forecast coverage states"
              >
                {forecastStates.map((state) => (
                  <button
                    type="button"
                    key={state}
                    className={state === 'missing' && forecastCount(state) ? 'has-gap' : undefined}
                    aria-label={`${label(state)} forecast evidence — ${count(forecastCount(state))} project records`}
                    onClick={() => onInspectForecast(state)}
                  >
                    <span>{label(state)}</span>
                    <strong>{count(forecastCount(state))}</strong>
                  </button>
                ))}
              </div>
              {!forecasts.length && (
                <p className="executive-overview__note">
                  No eligible financial projects are recorded.
                </p>
              )}
            </>
          )}
          <div className="executive-overview__footnote">
            <span>Company-wide · current human-approved WIP and declared currency required.</span>
            <button
              type="button"
              onClick={() =>
                open(Boolean(finance), () => onInspectForecast(), 'Project forecast evidence')
              }
            >
              Review evidence <ArrowUpRight aria-hidden="true" />
            </button>
          </div>
        </OverviewPanel>

        <OverviewPanel
          {...sectionProps('decisions')}
          title="Decisions & executive priorities"
          icon={<BriefcaseBusiness />}
          className="executive-overview__decisions"
          onInspect={() => open(Boolean(decisions), onInspectDecisions, 'Waiting source decisions')}
        >
          <div className="executive-overview__decision-summary">
            <div>
              <h3>CEO decision queue</h3>
              <strong>
                {decisions ? `${count(decisions.total)} source decisions waiting` : 'Unavailable'}
              </strong>
              <p>
                {decisions
                  ? `${decisions.items.length} preview rows · ${decisions.omittedKinds.length} source types omitted without access`
                  : 'Native decision sources unavailable or restricted.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                open(Boolean(decisions), onInspectDecisions, 'Waiting source decisions')
              }
            >
              Inspect decisions <ArrowUpRight aria-hidden="true" />
            </button>
          </div>
          {decisions?.items.length ? (
            <ul className="executive-overview__decision-list">
              {decisions.items.slice(0, 3).map((row) => (
                <li key={row.id}>
                  <Link to={row.href}>
                    {row.title}
                    <ArrowUpRight aria-hidden="true" />
                  </Link>
                  <span>
                    {count(row.ageHours)} hours waiting · {row.detail}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            decisions && (
              <p className="executive-overview__note">
                {decisions.total > 0
                  ? 'Waiting decisions counted; preview unavailable. Open the owning source register.'
                  : 'No waiting decisions in the permitted source cohort.'}
              </p>
            )
          )}
          {priorities}
          <p className="executive-overview__note">
            Review opens the owning workflow; approval and execution remain there.
          </p>
        </OverviewPanel>

        <OverviewPanel
          {...sectionProps('portfolio')}
          title="Portfolio exceptions & health"
          icon={<Building2 />}
          className="executive-overview__portfolio"
          onInspect={() =>
            inspect({
              id: 'portfolio-overview',
              label: 'Selected portfolio evidence',
              measure: 'Recorded project evidence',
              value: count(canReadProjects ? projects.length : null),
              basis: `${payload.coverage?.projectCohort ?? 'Selected recorded portfolio'}. Recorded model health is not measured schedule or safety performance.`,
              records: projects.map((row) => ({
                id: row.id,
                title: `${row.projectNumber} · ${row.name}`,
                subtitle: `${label(row.riskLevel)} recorded risk · ${healthScore(row) === null ? 'Health unavailable · no valid recorded model' : `${healthScore(row)}/100 recorded model health`}`,
                href: `/projects/overview?projectId=${encodeURIComponent(row.id)}`,
              })),
              recordsAvailable: canReadProjects,
              recordsUnavailableReason: 'Project source access is required.',
              viewAllHref: canReadProjects ? '/projects' : undefined,
            })
          }
          detail={
            canReadProjects
              ? `${exceptionRows.length} exception records`
              : 'Project records restricted'
          }
        >
          {canReadProjects ? (
            <>
              {exceptionRows.length ? (
                <table
                  className="executive-overview__table"
                  aria-label="Portfolio exception records"
                >
                  <thead>
                    <tr>
                      <th>Project / record</th>
                      <th className="numeric">Model health</th>
                      <th className="numeric">Recorded risk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exceptionRows.slice(0, 6).map((row) => (
                      <tr key={row.id}>
                        <th scope="row">
                          <button type="button" onClick={() => onInspectProject(row)}>
                            {row.name}
                          </button>
                          <small>{row.projectNumber}</small>
                        </th>
                        <td className="numeric">
                          {healthScore(row) === null ? 'Unavailable' : `${healthScore(row)} / 100`}
                        </td>
                        <td className="numeric">
                          <RiskBadge level={row.riskLevel.toLowerCase() as 'low'} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="executive-overview__note">
                  No selected records meet the exception rules; this does not establish that all
                  risks are measured.
                </p>
              )}
              {exceptionRows.length > 6 && (
                <p className="executive-overview__note">
                  6 of {exceptionRows.length} exception rows shown. Open Portfolio for the complete
                  selected cohort.
                </p>
              )}
            </>
          ) : null}
          <figure aria-label="Portfolio health distribution" className="executive-overview__health">
            {!canReadProjects ? (
              <SourceGap>Project records are restricted.</SourceGap>
            ) : !healthSourceAvailable ? (
              <SourceGap>
                Recorded model coverage is unavailable. Refresh the current source.
              </SourceGap>
            ) : (
              <>
                <figcaption>
                  Model health distribution · {projects.length} portfolio records
                </figcaption>
                <div className="executive-overview__status-grid executive-overview__status-grid--health">
                  {healthBands.map((band) => {
                    const bandLabel = band === 'UNAVAILABLE' ? 'No recorded model' : band;
                    const total =
                      payload.projectPerformance.healthDistribution.find((row) => row.band === band)
                        ?.count ?? 0;
                    return (
                      <button
                        type="button"
                        key={band}
                        aria-label={`${bandLabel} — ${total} project records`}
                        onClick={() => onInspectHealth(band)}
                      >
                        <span>{bandLabel}</span>
                        <strong>{total}</strong>
                      </button>
                    );
                  })}
                </div>
                {projects.length > 0 ? (
                  <div className="executive-overview__coverage-meter" aria-hidden="true">
                    {healthBands.map((band) => (
                      <span
                        key={band}
                        data-coverage-state={
                          band === 'UNAVAILABLE'
                            ? 'unavailable'
                            : band === '<60'
                              ? 'missing'
                              : 'recorded'
                        }
                        style={{
                          width: `${((payload.projectPerformance.healthDistribution.find((row) => row.band === band)?.count ?? 0) / projects.length) * 100}%`,
                        }}
                      />
                    ))}
                  </div>
                ) : null}
                <p className="executive-overview__note">{healthCoverage}</p>
              </>
            )}
          </figure>
          <div className="executive-overview__footnote">
            <span>Recorded model health is not verified schedule or safety performance.</span>
            {topProject && (
              <button type="button" onClick={() => onInspectProject(topProject)}>
                Inspect project evidence <ArrowUpRight aria-hidden="true" />
              </button>
            )}
            {canReadProjects && (
              <Link to="/projects">
                Open portfolio <ArrowUpRight aria-hidden="true" />
              </Link>
            )}
          </div>
        </OverviewPanel>

        <OverviewPanel
          {...sectionProps('growth')}
          title="Growth pipeline & bid outcomes"
          icon={<TrendingUp />}
          className="executive-overview__growth"
          onInspect={() =>
            inspect({
              id: 'growth-overview',
              label: 'Recorded growth source measures',
              measure: 'Native aggregate source measures',
              basis:
                'Lead and estimate populations are separate. Recorded bid outcomes are a different cohort. Counts are not a conversion funnel or weighted revenue.',
              records: [
                { id: 'leads', title: 'Recorded leads', subtitle: count(growth?.leads?.total) },
                {
                  id: 'estimates',
                  title: 'Recorded estimates',
                  subtitle: count(growth?.estimates?.total),
                },
                {
                  id: 'upcoming',
                  title: 'Lead bid dates within 30 days',
                  subtitle: count(growth?.leads?.dueWithin30Days),
                },
                {
                  id: 'undated',
                  title: 'Lead bid dates missing',
                  subtitle: count(growth?.leads?.undated),
                },
                {
                  id: 'outcomes',
                  title: 'Won / decided recorded outcomes',
                  subtitle: owner?.growth
                    ? `${count(owner.growth.wonCount)} / ${count(owner.growth.wonCount + owner.growth.lostCount)}`
                    : 'Unavailable',
                },
              ],
              recordsAvailable: Boolean(growth || owner?.growth),
              recordsUnavailableReason: 'Recorded growth sources are unavailable or restricted.',
              viewAllHref: hasPermission(permissions, PERMISSIONS.ESTIMATING_LEADS_READ)
                ? '/estimating/bid-intake'
                : undefined,
              viewAllLabel: 'Open native bid intake',
            })
          }
        >
          <dl className="executive-overview__rows">
            <ValueRow name="Recorded leads" value={count(growth?.leads?.total)} />
            <ValueRow name="Recorded estimates" value={count(growth?.estimates?.total)} />
            <ValueRow
              name="Lead bid dates within 30 days"
              value={count(growth?.leads?.dueWithin30Days)}
            />
            <ValueRow name="Lead bid dates missing" value={count(growth?.leads?.undated)} />
            <ValueRow
              name="Won / decided recorded outcomes"
              value={
                owner?.growth
                  ? `${count(owner.growth.wonCount)} / ${count(owner.growth.wonCount + owner.growth.lostCount)}`
                  : 'Unavailable'
              }
            />
            <ValueRow
              name="Hit rate"
              value={
                owner?.growth && owner.growth.wonCount + owner.growth.lostCount > 0
                  ? `${count(owner.growth.hitRatePercent)}%`
                  : 'Unavailable'
              }
            />
          </dl>
          <div className="executive-overview__footnote">
            <span>Lead, estimate and recorded outcome cohorts differ.</span>
            {hasPermission(permissions, PERMISSIONS.ESTIMATING_LEADS_READ) && (
              <Link to="/estimating/bid-intake">
                Open bid intake <ArrowUpRight aria-hidden="true" />
              </Link>
            )}
          </div>
        </OverviewPanel>

        <OverviewPanel
          {...sectionProps('operations')}
          title="Operating constraints"
          icon={<HardHat />}
          className="executive-overview__operations"
          onInspect={() => open(Boolean(operations), onInspectOperations, 'Operating exceptions')}
        >
          <dl className="executive-overview__rows executive-overview__rows--operations">
            <ValueRow name="Reported crews" value={count(field?.totalCrews)} />
            <ValueRow name="Blocked field tasks" value={count(operations?.blocked?.blockedTasks)} />
            <ValueRow
              name="Open safety observations"
              value={count(operations?.blocked?.openSafetyObservations)}
            />
            <ValueRow
              name="Missing site reports"
              value={count(field?.projectsWithoutDailyReport)}
            />
            <ValueRow
              name="Overdue quote requests"
              value={count(operations?.chasing?.overdueQuoteRequests)}
            />
          </dl>
          <p className="executive-overview__note">
            Labor capacity, equipment utilization and safety-rate denominators are unavailable.
            Reporting does not establish staffing demand.
          </p>
          <div className="executive-overview__footnote">
            <span>
              {field ? `${field.date} · ${field.timeZone}` : 'Field calendar unavailable'}
            </span>
            <button
              type="button"
              onClick={() => open(Boolean(operations), onInspectOperations, 'Operating exceptions')}
            >
              Inspect operating exceptions <ArrowUpRight aria-hidden="true" />
            </button>
          </div>
        </OverviewPanel>
      </div>

      <OverviewPanel
        {...sectionProps('coverage')}
        title="Data confidence & source coverage"
        icon={<Database />}
        className="executive-overview__coverage"
        onInspect={() =>
          inspect({
            id: 'executive-source-coverage',
            label: 'Executive source evidence and methodology',
            measure: 'Source availability and scope',
            basis:
              'Availability does not establish completeness. Assembly time is distinct from source observation, import and report dates. Portfolio filters do not change company-wide source scopes.',
            records: sources.map((source, index) => ({
              id: `executive-source-${index}`,
              title: source.label,
              subtitle: `${label(source.state)} · ${source.scope}. ${source.note}`,
              date: source.assembledAt ?? undefined,
            })),
            recordsAvailable: Boolean(sources.length),
            recordsUnavailableReason: 'Executive source composition was not returned.',
          })
        }
        detail="Availability does not establish completeness. Assembly time is distinct from source dates."
      >
        {sources.length ? (
          <table
            className="executive-overview__table executive-overview__source-table"
            aria-label="Executive source coverage"
          >
            <thead>
              <tr>
                <th>Source</th>
                <th>Availability</th>
                <th>Scope</th>
                <th>Qualification / methodology</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((source) => (
                <tr key={source.label}>
                  <th scope="row">{source.label}</th>
                  <td>
                    <Badge
                      className="!px-1 !py-0 !text-[10px] !leading-[14px]"
                      variant={source.state === 'available' ? 'neutral' : 'warning'}
                    >
                      {label(source.state)}
                    </Badge>
                  </td>
                  <td>{source.scope}</td>
                  <td>
                    <details>
                      <summary>
                        {source.note.split(/(?<=\.)\s/)[0] || 'Inspect source qualification'}
                      </summary>
                      <p>{source.note}</p>
                      <p>
                        Assembled{' '}
                        {source.assembledAt
                          ? new Date(source.assembledAt).toLocaleString()
                          : 'time unavailable'}
                        .
                      </p>
                    </details>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <SourceGap>
            Executive source composition was not returned. Refresh to read the current contract.
          </SourceGap>
        )}
      </OverviewPanel>
    </div>
  );
}

function OverviewPanel({
  title,
  icon,
  detail,
  className,
  children,
  onInspect,
  id,
  hidden,
  tabLabelId,
  sectionId,
}: {
  title: string;
  icon: ReactNode;
  detail?: string;
  className?: string;
  children: ReactNode;
  onInspect: () => void;
  id: string;
  hidden: boolean;
  tabLabelId?: string;
  sectionId: string;
}) {
  const heading = useId();
  return (
    <section
      id={id}
      hidden={hidden}
      role={tabLabelId ? 'tabpanel' : undefined}
      className={`executive-overview__panel glass hx-data-surface ${className ?? ''}`}
      data-data-surface="executive-section"
      data-native-inspection=""
      data-overview-section={sectionId}
      aria-labelledby={tabLabelId ?? heading}
      onClick={(event) => {
        const target = event.target;
        if (
          !(target instanceof Element) ||
          target.closest('a, button, input, select, textarea, summary, details, [role="button"]')
        )
          return;
        if (window.getSelection()?.toString()) return;
        onInspect();
      }}
    >
      <header className="hx-section-header executive-overview__panel-header">
        <h2 id={heading} className="hx-section-title">
          {icon}
          {title}
        </h2>
        {detail && <span>{detail}</span>}
        <button
          className="executive-overview__expand"
          type="button"
          aria-label={`Expand ${title}`}
          onClick={onInspect}
        >
          <Maximize2 aria-hidden="true" />
        </button>
      </header>
      <div
        className="executive-overview__panel-body"
        tabIndex={0}
        role="region"
        aria-label={`${title} details`}
      >
        {children}
      </div>
    </section>
  );
}
function ValueRow({ name, value }: { name: string; value: string }) {
  return (
    <div>
      <dt>{name}</dt>
      <dd>{value}</dd>
    </div>
  );
}
function SourceGap({ children }: { children: ReactNode }) {
  return (
    <p className="executive-overview__gap">
      <CircleAlert aria-hidden="true" />
      {children}
    </p>
  );
}
